import type { DropboxArchiveStatus, InvoiceListItem } from '../types';

/**
 * Vue "résumée" d'une facture pour l'affichage liste : on tire les champs
 * utiles depuis validated_data (si validé/créé) ou extracted_data (sinon).
 * Pas de tri côté serveur : tout se fait sur le tableau local (limite=100).
 */
export interface InvoiceSummary {
  id: string;
  filename: string;
  source: InvoiceListItem['source'];
  source_reference: string | null;
  status: InvoiceListItem['status'];
  created_at: string;
  // Champs dérivés (peuvent être null si pas encore d'extraction).
  invoice_date: string | null;
  vendor_name: string | null;
  invoice_number: string | null;
  total_ht: number | null;
  total_ttc: number | null;
  dropbox_archive_status: DropboxArchiveStatus;
  dropbox_archive_error: string | null;
}

interface ExtractedShape {
  supplier?: { name?: string };
  invoice?: { number?: string; date?: string };
  totals?: { total_ht?: number; total_ttc?: number };
}

interface ValidatedShape {
  invoice_number?: string;
  invoice_date?: string;
  lines?: Array<{ quantity: number; unit_price_ht: number; vat_rate: number | null }>;
}

function safeParse<T>(s: string | null | undefined): T | null {
  if (!s) return null;
  try {
    return JSON.parse(s) as T;
  } catch {
    return null;
  }
}

function round2(n: number): number {
  return Math.round(n * 100) / 100;
}

/**
 * Calcule l'ensemble des IDs de factures qui sont des doublons POTENTIELS :
 * même fournisseur (normalisé) + même numéro de facture, à au moins 2
 * exemplaires, et non encore créées dans Zoho (les 'created' ne sont plus
 * candidates car déjà comptabilisées). Renvoie un Map id → liste des autres
 * ids du groupe (pour le tooltip).
 */
export function computeDuplicateGroups(
  summaries: InvoiceSummary[],
): Map<string, string[]> {
  const groups = new Map<string, InvoiceSummary[]>();
  for (const s of summaries) {
    if (!s.vendor_name || !s.invoice_number) continue;
    if (s.status === 'rejected') continue;
    const key = `${s.vendor_name.trim().toLowerCase()}|${s.invoice_number.trim().toLowerCase()}`;
    const arr = groups.get(key) ?? [];
    arr.push(s);
    groups.set(key, arr);
  }
  const result = new Map<string, string[]>();
  for (const arr of groups.values()) {
    if (arr.length < 2) continue;
    for (const s of arr) {
      result.set(
        s.id,
        arr.filter((o) => o.id !== s.id).map((o) => o.id),
      );
    }
  }
  return result;
}

export function summarize(inv: InvoiceListItem): InvoiceSummary {
  const extracted = safeParse<ExtractedShape>(inv.extracted_data);
  const validated = safeParse<ValidatedShape>(inv.validated_data);

  let totalHt: number | null = null;
  let totalTtc: number | null = null;
  if (validated?.lines && validated.lines.length > 0) {
    let ht = 0;
    let vat = 0;
    for (const l of validated.lines) {
      const lineHt = round2(l.quantity * l.unit_price_ht);
      const lineVat = round2(lineHt * ((l.vat_rate ?? 0) / 100));
      ht += lineHt;
      vat += lineVat;
    }
    totalHt = round2(ht);
    totalTtc = round2(ht + vat);
  } else if (extracted?.totals) {
    totalHt = extracted.totals.total_ht ?? null;
    totalTtc = extracted.totals.total_ttc ?? null;
  }

  return {
    id: inv.id,
    filename: inv.filename,
    source: inv.source,
    source_reference: inv.source_reference,
    status: inv.status,
    created_at: inv.created_at,
    invoice_date: validated?.invoice_date ?? extracted?.invoice?.date ?? null,
    vendor_name: extracted?.supplier?.name ?? null,
    invoice_number: validated?.invoice_number ?? extracted?.invoice?.number ?? null,
    total_ht: totalHt,
    total_ttc: totalTtc,
    dropbox_archive_status: inv.dropbox_archive_status,
    dropbox_archive_error: inv.dropbox_archive_error,
  };
}
